import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { createBooking } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const inputCls = "w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none";
const labelCls = "mb-1.5 block text-sm text-ink-2";

export default function BookingForm({ bookingType = "pooja", formType = "venue", service }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "", preferred_date: "", city: "",
    birth_date: "", birth_time: "", birth_place: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const isConsult = formType === "consult";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await createBooking({
        booking_type: bookingType,
        service_slug: service?.slug || "",
        service_name: service?.name || (isConsult ? "Consultation Request" : ""),
        ...form,
      });
      setDone(res.booking_id);
      toast("Request received", { description: "Our team will reach out to confirm." });
    } catch (err) {
      toast.error("Could not submit", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="border border-line bg-[var(--surface-alt)]/50 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-saffron" />
        <h3 className="mt-4 font-serif-latin text-2xl text-ink">Request received</h3>
        <p className="mt-2 text-ink-2">Reference: <span className="font-mono text-sm">{done}</span></p>
        <p className="mt-1 text-sm text-ink-3">Our team will contact you to confirm the details. No payment is taken online.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="booking-form" className="space-y-4 border border-line bg-surface p-6 md:p-8">
      <h3 className="font-serif-latin text-2xl text-ink">{isConsult ? "Request a Consultation" : "Request this Booking"}</h3>
      <p className="text-sm text-ink-3">Submit a request — no online payment. Our team will contact you to confirm.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={labelCls}>Name *</label><input data-testid="booking-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>Phone *</label><input data-testid="booking-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>Email</label><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>City</label><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></div>
      </div>
      {isConsult ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <div><label className={labelCls}>Birth date</label><input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Birth time</label><input type="time" value={form.birth_time} onChange={(e) => set("birth_time", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Birth place</label><input value={form.birth_place} onChange={(e) => set("birth_place", e.target.value)} className={inputCls} /></div>
        </div>
      ) : (
        <div><label className={labelCls}>Preferred date</label><input data-testid="booking-date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className={inputCls} /></div>
      )}
      <div><label className={labelCls}>Message / notes</label><textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} /></div>
      <button data-testid="booking-submit" disabled={busy} className="w-full bg-[var(--saffron)] py-3 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
        {busy ? "Submitting…" : "Submit Request"}
      </button>
    </form>
  );
}
